"use client"

import { useMemo, useState } from "react"
import { Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { INSURERS, INSURERS_VERIFIED_ON } from "@/lib/breach/insurers"
import { searchInsurers } from "@/lib/breach/insurer-search"

type Tab = "have" | "need"

export function InsuranceDirectory() {
  const [tab, setTab] = useState<Tab>("have")
  const [q, setQ] = useState("")
  const results = useMemo(() => searchInsurers(INSURERS, q), [q])
  const quotes = INSURERS.filter((i) => i.quoteUrl)

  return (
    <div>
      <div role="tablist" aria-label="Insurance" className="mb-6 flex flex-wrap gap-2 border-b border-border">
        {([["have", "I already have a policy"], ["need", "I need coverage"]] as const).map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={cn("rounded-t-md px-4 py-2 text-sm font-medium", tab === id ? "border-b-2 border-accent text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {label}
          </button>
        ))}
      </div>

      {tab === "have" && (
        <section aria-labelledby="have-h">
          <h2 id="have-h" className="text-2xl font-bold">Find your cyber insurer&apos;s claims contact</h2>
          <p className="mt-2 text-muted-foreground">
            <strong className="text-foreground">Use the claims number or email on your own policy first</strong>, or call your broker or agent. Many insurers want notice through a specific channel,
            and the contact for your policy may differ from the general one below. These details came from each insurer&apos;s own website on {INSURERS_VERIFIED_ON} and may have changed.
          </p>
          <label className="mt-5 flex items-center gap-2 rounded-lg border border-border bg-background p-3">
            <Search className="h-5 w-5 text-muted-foreground" aria-hidden />
            <span className="sr-only">Search insurers by name</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type your insurer's name, or the company on your policy"
              className="w-full bg-transparent text-base outline-none" autoComplete="off" />
          </label>
          <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">{results.length} of {INSURERS.length} insurers{q ? ` match “${q}”` : ""}</p>

          {results.length === 0 && (
            <div className="mt-4 rounded-lg border border-border p-4 text-sm text-muted-foreground">
              We don&apos;t have that insurer. Look on your policy&apos;s declarations page or notice-of-claim form, or call your broker or agent. The company named on your policy may differ from the brand you know.
            </div>
          )}

          <ul className="mt-4 grid gap-4">
            {results.map((i) => (
              <li key={i.name} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-lg font-semibold">{i.name}</h3>
                  <span className="text-xs text-muted-foreground">{i.type}{i.parentGroup ? ` · ${i.parentGroup}` : ""}</span>
                </div>
                <dl className="mt-3 grid gap-1 text-sm sm:grid-cols-[8rem_1fr]">
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd>
                    {i.phone ? <><a className="font-medium underline" href={`tel:${i.phone.replace(/[^\d+]/g, "")}`}>{i.phone}</a>{i.phoneNote && <span className="block text-xs text-muted-foreground">{i.phoneNote}</span>}</>
                      : <span className="text-muted-foreground">No public cyber claims line found. {i.phoneNote ?? "Use your policy or your broker."}</span>}
                  </dd>
                  {i.claimsEmail && (<><dt className="text-muted-foreground">Claims email</dt><dd><a className="underline" href={`mailto:${i.claimsEmail}`}>{i.claimsEmail}</a></dd></>)}
                  {i.claimsUrl && (<><dt className="text-muted-foreground">Report a claim</dt><dd><a className="underline" href={i.claimsUrl} target="_blank" rel="noopener nofollow">Insurer&apos;s claims page</a></dd></>)}
                </dl>
                {i.instructions && <p className="mt-2 text-xs text-muted-foreground">Insurer says: {i.instructions}</p>}
                {i.panelNote && <p className="mt-1 text-xs text-muted-foreground">Approved vendors: {i.panelNote}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === "need" && (
        <section aria-labelledby="need-h">
          <h2 id="need-h" className="text-2xl font-bold">Getting cyber insurance after a breach</h2>
          <div className="mt-3 grid gap-3 text-muted-foreground">
            <p>
              Talk to a <strong className="text-foreground">licensed insurance broker or agent</strong>. Coverage for something that has already happened is a question for your broker and the policy wording, not for this site.
              Applications usually ask about past incidents, so answer them accurately and have your broker help.
            </p>
            <p>
              One insurer says on its own cyber page that its policy does not cover known events that occurred before the policy period began. Other insurers&apos; wording differs, and we found no regulator guidance
              that sets out the rules, so we don&apos;t state them here. A new policy may help you with the next incident even if it doesn&apos;t help with this one.
            </p>
            <p>If you already have a cyber policy, <strong className="text-foreground">tell your insurer promptly</strong>. The National Association of Insurance Commissioners (NAIC) says insurers typically require notice before a ransom payment and that skipping it can mean losing coverage.</p>
          </div>

          <h3 className="mt-8 text-lg font-semibold">Check who you&apos;re dealing with</h3>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-muted-foreground">
            <li><a className="underline" href="https://content.naic.org/state-insurance-departments" target="_blank" rel="noopener">NAIC: find your state insurance department</a>. It has an agent search, and your state department can confirm whether a broker or agent is licensed.</li>
            <li><a className="underline" href="https://content.naic.org/insurance-topics/ransomware" target="_blank" rel="noopener">NAIC: ransomware and insurance</a>.</li>
          </ul>

          {quotes.length > 0 && (
            <>
              <h3 className="mt-8 text-lg font-semibold">Insurers&apos; own quote pages</h3>
              <p className="mt-1 text-sm text-muted-foreground">Listed for convenience, not recommended or ranked. We receive nothing if you use them. Check each insurer&apos;s terms, including how it treats known incidents.</p>
              <ul className="mt-3 grid gap-2">
                {quotes.map((i) => (
                  <li key={i.name}><a className="underline" href={i.quoteUrl!} target="_blank" rel="noopener nofollow">{i.name}: get a quote</a></li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  )
}
